// ============================================================
// TMR Portal Admin — the four states every screen implements
// (skill `frontend-architecture`, Regra 7). Copy is PT-BR by design.
// ============================================================
import type { ReactNode } from 'react';
import { ApiError } from '@tmr/api-client';
import { Icons, StrokeIcon } from '../lib/icons';
import { Btn } from './primitives';

/** Props for {@link Skeleton}. */
interface SkeletonProps {
  /** Number of placeholder rows to render. */
  rows?: number;
}

/** Placeholder shown while a query is loading, sized like the content it replaces. */
export function Skeleton({ rows = 5 }: SkeletonProps) {
  return (
    <div aria-busy="true" aria-label="Carregando" style={{ display: 'grid', gap: 10 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            height: 44,
            borderRadius: 'var(--radius-sm)',
            background: 'linear-gradient(90deg, var(--cream-100) 25%, var(--cream-200) 50%, var(--cream-100) 75%)',
            backgroundSize: '200% 100%',
            animation: 'paShimmer 1.4s var(--ease-out) infinite',
          }}
        />
      ))}
    </div>
  );
}

/** Props for {@link EmptyState}. */
interface EmptyStateProps {
  /** What the operator sees — written for this screen, never a generic "sem dados". */
  message: string;
  /** Optional hint on what to do next. */
  hint?: string;
  /** Optional primary action, e.g. the button that creates the first record. */
  action?: ReactNode;
}

/** Shown when a query succeeded and legitimately returned nothing. */
export function EmptyState({ message, hint, action }: EmptyStateProps) {
  return (
    <div style={frameStyle}>
      <StrokeIcon d={Icons.doc} size={22} />
      <p style={{ margin: '10px 0 0', fontFamily: 'var(--font-sans)', fontSize: 14.5, color: 'var(--fg-muted)' }}>
        {message}
      </p>
      {hint && <p style={hintStyle}>{hint}</p>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

/** Props for {@link ErrorState}. */
interface ErrorStateProps {
  /** The failure to explain; an {@link ApiError} already carries operator-facing PT-BR text. */
  error: unknown;
  /** Retries the failed query. */
  onRetry?: () => void;
}

/**
 * Shown when a query failed. A missing permission is not an error the operator can retry away, so it
 * gets its own wording and no retry button — the server is right to refuse (06 §6).
 */
export function ErrorState({ error, onRetry }: ErrorStateProps) {
  const apiError = error instanceof ApiError ? error : null;
  const forbidden = apiError?.isForbidden ?? false;

  return (
    <div style={frameStyle} role="alert">
      <StrokeIcon d={forbidden ? Icons.shield : Icons.alert} size={22} />
      <p style={{ margin: '10px 0 0', fontFamily: 'var(--font-sans)', fontSize: 14.5, color: 'var(--ink-900)' }}>
        {forbidden ? 'Você não tem permissão para ver isto.' : 'Não foi possível carregar os dados.'}
      </p>
      {apiError && <p style={hintStyle}>{apiError.detail}</p>}
      {onRetry && !forbidden && (
        <div style={{ marginTop: 14 }}>
          <Btn onClick={onRetry}>Tentar novamente</Btn>
        </div>
      )}
    </div>
  );
}

/** Props for {@link NoBackendNotice}. */
interface NoBackendNoticeProps {
  /** What this screen would show once wired, e.g. "os contratos do tmr_v2". */
  what: string;
  /** Optional sentence naming what does work today, so the operator is left somewhere to go. */
  instead?: ReactNode;
}

/**
 * Shown at the top of a screen that is still a mockup — no endpoint behind it (09 §9.4).
 *
 * A published environment makes this a matter of correctness, not politeness: an operator cannot tell an
 * empty list that means "nothing exists" from one that means "nothing is connected", and a create button
 * that quietly forgets the record on reload reads as a bug in the save. Saying it costs one paragraph.
 * Pair it with disabling the actions the screen cannot honour (skill `frontend-architecture`, Regra 5).
 */
export function NoBackendNotice({ what, instead }: NoBackendNoticeProps) {
  return (
    <div
      style={{
        padding: '10px 14px',
        border: '1px dashed var(--border-default)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--cream-100)',
        fontFamily: 'var(--font-sans)',
        fontSize: 12.5,
        lineHeight: 1.55,
        color: 'var(--fg-muted)',
      }}
    >
      Esta tela ainda não está ligada ao backend — {what} não aparecem aqui, e o cadastro está
      indisponível.{instead ? <> {instead}</> : null}
    </div>
  );
}

const frameStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center' as const,
  padding: '38px 24px',
  color: 'var(--stone-400)',
};

const hintStyle = {
  margin: '4px 0 0',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  color: 'var(--fg-subtle)',
  maxWidth: 420,
};
